import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertCircle, Bell } from 'lucide-react';

interface NotificationPreferences {
  email_frequency?: string;
  alert_types?: string[];
}

export function NotificationSettings() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const alertTypes = [
    { value: 'urgent', label: 'Urgent Alerts' },
    { value: 'compliance_deadline', label: 'Compliance Deadlines' },
    { value: 'new_regulation', label: 'New Regulations' },
    { value: 'task_assigned', label: 'Task Assignments' },
  ];

  const emailFrequencies = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Summary' },
    { value: 'never', label: 'Never' },
  ];

  // Load current preferences
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const prefs = await apiClient.getUserPreferences();
      setPreferences(prefs.notification_settings || {});
    } catch (err: any) {
      setError(err.message || 'Failed to load preferences');
      console.error('Error loading preferences:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await apiClient.updateUserPreferences({
        notification_settings: preferences,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAlertTypeToggle = (alertType: string) => {
    const current = preferences.alert_types || [];
    const updated = current.includes(alertType)
      ? current.filter(a => a !== alertType)
      : [...current, alertType];
    setPreferences({ ...preferences, alert_types: updated });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Bell className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Notification Preferences</h2>
        </div>

        <div className="space-y-6">
          {/* Email Frequency */}
          <div>
            <Label htmlFor="emailFrequency">Email Notification Frequency</Label>
            <p className="text-sm text-muted-foreground mb-2">
              How often would you like to receive email notifications?
            </p>
            <select
              id="emailFrequency"
              value={preferences.email_frequency || 'daily'}
              onChange={(e) => setPreferences({ ...preferences, email_frequency: e.target.value })}
              disabled={isSaving}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {emailFrequencies.map((freq) => (
                <option key={freq.value} value={freq.value}>
                  {freq.label}
                </option>
              ))}
            </select>
          </div>

          {/* Alert Types */}
          <div>
            <Label>Alert Types</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Select which types of alerts you want to receive
            </p>
            <div className="space-y-2">
              {alertTypes.map((alertType) => (
                <div key={alertType.value} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`alert-${alertType.value}`}
                    checked={(preferences.alert_types || []).includes(alertType.value)}
                    onChange={() => handleAlertTypeToggle(alertType.value)}
                    disabled={isSaving}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <Label htmlFor={`alert-${alertType.value}`} className="cursor-pointer font-normal">
                    {alertType.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Notification preferences saved successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end pt-4">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

